import {
  Button,
  ButtonLevel,
  ButtonSize,
  SelectedFile,
  SelectedFileStatus,
  Text,
  TextColor,
  TextSize,
  TextWeight,
} from '@vizit/atoms';
import { PageIcon } from '@vizit/icons';
import { Dialog, type DialogProps, DropZone } from '@vizit/molecules';
import { borderColor } from '@vizit/theme';
import { formatFileSize } from '@vizit/utils';
import * as React from 'react';

interface BulkImportDialogProps extends Omit<DialogProps, 'children'> {
  /**
   * Content for the trigger button. If not provided, the button will not be rendered.
   *
   * @optional
   * @type {React.ReactNode}
   */
  triggerButtonContent?: React.ReactNode;
  /**
   * Callback when the import action is confirmed
   * @param file - The CSV file to import
   */
  onSubmit: (file: File) => Promise<void>;
  /**
   * Whether to hide the trigger button
   * Set to true when controlled externally
   */
  hideTrigger?: boolean;
  /**
   * The retailer key (e.g., "amazon", "walmart"). Used for template example content.
   */
  retailer?: string;
  /**
   * The region key (e.g., "us", "uk"). Used for template example content.
   */
  region?: string;
  /**
   * The label for the item ID field (e.g., "ASINs", "Item IDs"). Sourced from the API's item_id_label.
   * Defaults to "ASINs" when not provided.
   */
  itemIdLabel?: string;
}

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
const MIN_FILE_SIZE = 40; // 40B
const ACCEPTED_FILE_TYPES = ['text/csv'];

const EXAMPLE_ITEM_IDS: Record<string, string> = {
  amazon: 'B00EXAMPLE',
  walmart: '123456789',
};

const DEFAULT_EXAMPLE_ITEM_ID = EXAMPLE_ITEM_IDS.amazon;

const buildTemplateContent = (retailer?: string, region?: string) => {
  const exampleItemId = (retailer && EXAMPLE_ITEM_IDS[retailer]) || DEFAULT_EXAMPLE_ITEM_ID;
  const exampleRetailer = retailer || 'amazon';
  const exampleRegion = region || 'us';
  return `Item ID,Retailer,Region\n${exampleItemId},${exampleRetailer},${exampleRegion}\n`;
};

/**
 * Downloads a template CSV file with a retailer-appropriate example row
 */
const downloadTemplate = (retailer?: string, region?: string) => {
  const content = buildTemplateContent(retailer, region);
  const blob = new Blob([content], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'Vizit_Bulk_PDP_import.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Dialog component for importing multiple PDPs via CSV file upload
 *
 * @param props - The component props
 * @param props.isOpen - Whether the dialog is open
 * @param props.onOpenChange - Callback when dialog open state changes
 * @param props.onSubmit - Callback function when import is confirmed with the CSV file
 * @param props.triggerButtonContent - Content for the trigger button. If not provided, the button will not be rendered.
 *
 * @example
 * <BulkImportDialog
 *   isOpen={isOpen}
 *   onOpenChange={setIsOpen}
 *   onSubmit={handleImport}
 * />
 */
function BulkImportDialog(props: BulkImportDialogProps) {
  const {
    isOpen,
    onOpenChange,
    onSubmit,
    triggerButtonContent,
    hideTrigger = false,
    retailer,
    region,
    itemIdLabel = 'ASINs',
    ...other
  } = props;

  const templateContent = buildTemplateContent(retailer, region);

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);

  const handleClose = React.useCallback(() => {
    if (!isSubmitting) {
      onOpenChange?.(false);
    }
  }, [onOpenChange, isSubmitting]);

  const handleFilesChange = (files: File[]) => {
    const file = files[0];
    setSelectedFile(file || null);
    setSubmitError(null);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setSubmitError(null);
  };

  const handleDialogOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open && !isSubmitting) {
        setSelectedFile(null);
        setSubmitError(null);
      }
      onOpenChange?.(open);
    },
    [onOpenChange, isSubmitting],
  );

  return (
    <Dialog isOpen={isOpen} onOpenChange={handleDialogOpenChange} {...other}>
      {triggerButtonContent != null && !hideTrigger && (
        <Button level={ButtonLevel.Primary}>{triggerButtonContent}</Button>
      )}
      <Dialog.Overlay>
        <Dialog.Content dialogClassName="gap-12" data-testid="bulk-import-dialog">
          {({ close }) => {
            const handleSubmit = async () => {
              if (!selectedFile) return;

              setIsSubmitting(true);
              setSubmitError(null);

              try {
                await onSubmit(selectedFile);
                // Close dialog on success
                if (onOpenChange) {
                  handleClose();
                } else {
                  close();
                }
                // Reset state after successful submit
                setSelectedFile(null);
                setSubmitError(null);
              } catch (error) {
                // Keep dialog open and show error
                console.error(error);
                setSubmitError(
                  error instanceof Error
                    ? cleanErrorMessage(error.message)
                    : 'An error occurred while importing the file.',
                );
              } finally {
                setIsSubmitting(false);
              }
            };

            return (
              <>
                <Dialog.Header>
                  <Dialog.Title>Bulk import product pages</Dialog.Title>
                  <Dialog.Subtitle color={TextColor.Tertiary}>
                    Download the template and enter your list of {itemIdLabel}. Then upload to this modal.
                  </Dialog.Subtitle>
                </Dialog.Header>
                <div className="px-12 space-y-12">
                  {/* Template Download Section */}
                  <div className="flex items-center justify-between p-5 border border-secondary rounded-lg">
                    <div className="flex items-center gap-5 pl-5 pr-2">
                      <div className="flex flex-col gap-2">
                        <PageIcon size={40} color={borderColor.secondary} />
                        <div className="relative bottom-15 right-3">
                          <div className="px-[3px] py-1 absolute bg-success-700">
                            <Text className="text-white text-[10px] font-bold leading-normal">CSV</Text>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <Text className="text-sm font-medium">Vizit_Bulk_PDP_import.csv</Text>
                        <Text color={TextColor.Tertiary} className="text-xs">
                          {formatFileSize(templateContent.length)}
                        </Text>
                      </div>
                    </div>
                    <Button
                      level={ButtonLevel.Secondary}
                      size={ButtonSize.Sm}
                      onPress={() => downloadTemplate(retailer, region)}
                      data-testid="bulk-import-download-template"
                    >
                      Download
                    </Button>
                  </div>

                  {/* Upload CSV Section */}
                  <div className="space-y-5">
                    <Text size={TextSize.Sm} weight={TextWeight.Regular} color={TextColor.Secondary}>
                      Upload CSV
                    </Text>
                    <DropZone.Provider
                      acceptedFileTypes={ACCEPTED_FILE_TYPES}
                      allowsMultiple={false}
                      maxFileSize={MAX_FILE_SIZE}
                      onFilesChange={handleFilesChange}
                      minFileSize={MIN_FILE_SIZE}
                    >
                      <DropZone aria-label="Upload CSV" data-testid="bulk-import-dropzone" />
                      <DropZone.SelectedFiles aria-label="Selected CSV file">
                        {({ file, onRemove, className }) => (
                          <>
                            <SelectedFile
                              file={file}
                              status={isSubmitting ? SelectedFileStatus.Uploading : SelectedFileStatus.Ready}
                              onRemove={() => {
                                handleRemoveFile();
                                onRemove(file);
                              }}
                              className={className}
                            />
                            {selectedFile && !submitError && (
                              <Text size={TextSize.Sm} color={TextColor.Tertiary} showEllipsis={false}>
                                You will be notified via email when your products are finished processing and available
                                for optimization.
                              </Text>
                            )}
                            {submitError && (
                              <Text
                                role="alert"
                                size={TextSize.Sm}
                                color={TextColor.Danger}
                                showEllipsis={false}
                                data-testid="bulk-import-error"
                              >
                                {submitError}
                              </Text>
                            )}
                          </>
                        )}
                      </DropZone.SelectedFiles>
                    </DropZone.Provider>
                  </div>
                </div>
                <Dialog.Footer>
                  <Button
                    className="w-full"
                    size={ButtonSize.Lg}
                    level={ButtonLevel.Secondary}
                    onPress={onOpenChange ? handleClose : close}
                    isDisabled={isSubmitting}
                    data-testid="bulk-import-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="w-full"
                    size={ButtonSize.Lg}
                    level={ButtonLevel.Primary}
                    onPress={handleSubmit}
                    isDisabled={!selectedFile || isSubmitting}
                    isLoading={isSubmitting}
                    data-testid="bulk-import-submit"
                  >
                    Score product pages
                  </Button>
                </Dialog.Footer>
              </>
            );
          }}
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}

const cleanErrorMessage = (errorMessage: string) => {
  try {
    if (errorMessage.includes('{"')) {
      const json = JSON.parse(errorMessage.slice(errorMessage.indexOf('{"'), errorMessage.lastIndexOf('}') + 1));
      if (json.detail) {
        return `Error: ${json.detail}`;
      }
      return 'An error occurred while importing the file.';
    }
    return errorMessage;
  } catch (error) {
    console.error(error);
    return 'An error occurred while importing the file.';
  }
};

export { BulkImportDialog };
export type { BulkImportDialogProps };
